import { z } from 'zod'
import type { PaletteColor } from './schemas.ts'

/**
 * Roles stored on `users.role`.
 * - `viewer`: read-only HR queue (non-confidential tickets, public comments only).
 * - `member`: PRD "Employee"; default for every new sign-up.
 * - `admin`: PRD "HR team". With `isHrLead`, PRD "HR lead".
 */
export const ROLES = ['viewer', 'member', 'admin'] as const
export const roleSchema = z.enum(ROLES)
export type Role = z.infer<typeof roleSchema>

/** Minimal identity the permission helpers need. */
export interface Actor {
  id: string
  role: Role
  /** Only meaningful for admins; ignored for other roles. */
  isHrLead: boolean
  /** Archived people can do nothing. */
  archived?: boolean
}

/**
 * Every action governed by the PRD "Permissions matrix". Ticket-level checks that depend on
 * the ticket itself (whose it is, who it's assigned to, confidential) live in the
 * `canSeeTicket` / `canCommentOnTicket` / `canReopenTicket` helpers below.
 */
export const PERMISSIONS = [
  /** Raise a ticket for yourself. */
  'ticket.create.self',
  /** Log a ticket on behalf of an employee. */
  'ticket.create.onBehalf',
  /** Tick "Keep this confidential" while raising a ticket. */
  'ticket.confidential.atCreate',
  /** See tickets you raised (My tickets). */
  'ticket.viewOwn',
  /** See every non-confidential ticket. */
  'ticket.viewAll',
  /** See confidential tickets assigned to you. */
  'ticket.viewConfidential.assigned',
  /** See every confidential ticket (HR lead). */
  'ticket.viewConfidential.all',
  /** Post public comments (employees: on their own tickets only). */
  'ticket.comment.public',
  /** See internal notes. */
  'ticket.note.view',
  /** Post internal notes. */
  'ticket.note.internal',
  /** Change status, assignee, priority, category, ETA. */
  'ticket.edit.fields',
  /** Mark or unmark an existing ticket confidential. */
  'ticket.confidential.toggle',
  /** Reopen your own resolved/closed ticket with the Reopen button. */
  'ticket.reopen.own',
  /** Upload attachments (employees: on their own tickets only). */
  'ticket.attach',
  /** Use the HR list and board views with filters. */
  'queue.view',
  /** Open Settings. */
  'settings.view',
  /** Change Settings (categories, priorities, statuses, people details). */
  'settings.edit',
  /** List people. */
  'users.view',
  /** Edit people: team/role text, archive/restore employees. */
  'users.manage',
  /** Change access (role) and the HR lead flag; archive HR members (HR lead only). */
  'users.manageRoles',
] as const
export type Permission = (typeof PERMISSIONS)[number]

/** The four effective access levels the PRD talks about. */
export type AccessLevel = 'viewer' | 'employee' | 'hr' | 'hrLead'

export function accessLevel(actor: Pick<Actor, 'role' | 'isHrLead'>): AccessLevel {
  if (actor.role === 'admin') return actor.isHrLead ? 'hrLead' : 'hr'
  return actor.role === 'member' ? 'employee' : 'viewer'
}

export function isHrLead(actor: Pick<Actor, 'role' | 'isHrLead'>): boolean {
  return actor.role === 'admin' && actor.isHrLead
}

const V = 'viewer'
const E = 'employee'
const H = 'hr'
const L = 'hrLead'

/** Who holds each permission. Source: PRD "Permissions matrix" (+ viewer = look only). */
export const PERMISSION_MATRIX: Record<Permission, readonly AccessLevel[]> = {
  'ticket.create.self': [E, H, L],
  'ticket.create.onBehalf': [H, L],
  'ticket.confidential.atCreate': [E, H, L],
  'ticket.viewOwn': [E, H, L],
  'ticket.viewAll': [V, H, L],
  'ticket.viewConfidential.assigned': [H, L],
  'ticket.viewConfidential.all': [L],
  'ticket.comment.public': [E, H, L],
  'ticket.note.view': [H, L],
  'ticket.note.internal': [H, L],
  'ticket.edit.fields': [H, L],
  'ticket.confidential.toggle': [H, L],
  'ticket.reopen.own': [E, H, L],
  'ticket.attach': [E, H, L],
  'queue.view': [V, H, L],
  'settings.view': [H, L],
  'settings.edit': [H, L],
  'users.view': [H, L],
  'users.manage': [H, L],
  'users.manageRoles': [L],
}

export function isRole(value: unknown): value is Role {
  return roleSchema.safeParse(value).success
}

/** True when `role` is listed in `allowed` (or `allowed` is empty/undefined, meaning everyone). */
export function hasAnyRole(role: Role, allowed?: readonly Role[]): boolean {
  return !allowed || allowed.length === 0 || allowed.includes(role)
}

const RANK: Record<Role, number> = { viewer: 0, member: 1, admin: 2 }

/** Ordinal comparison; useful for coarse UI gating only. */
export function roleAtLeast(role: Role, min: Role): boolean {
  return RANK[role] >= RANK[min]
}

/** Whether `actor` holds `permission`. Archived actors hold nothing. */
export function can(actor: Actor | null | undefined, permission: Permission): boolean {
  if (!actor || actor.archived) return false
  return PERMISSION_MATRIX[permission].includes(accessLevel(actor))
}

/** The ticket fields visibility depends on. */
export interface TicketAccessInfo {
  raisedBy: string
  assignee: string | null
  confidential: boolean
}

/**
 * PRD "Confidential tickets" + "Visibility rule":
 * - the person who raised a ticket always sees it;
 * - employees see only their own;
 * - viewers see non-confidential tickets;
 * - HR team sees non-confidential tickets plus confidential ones assigned to them;
 * - HR leads see everything.
 */
export function canSeeTicket(actor: Actor | null | undefined, ticket: TicketAccessInfo): boolean {
  if (!actor || actor.archived) return false
  if (ticket.raisedBy === actor.id) return true
  if (!can(actor, 'ticket.viewAll')) return false
  if (!ticket.confidential) return true
  if (can(actor, 'ticket.viewConfidential.all')) return true
  return can(actor, 'ticket.viewConfidential.assigned') && ticket.assignee === actor.id
}

/** Public comments: HR on any ticket they can see; employees on their own tickets. */
export function canCommentOnTicket(
  actor: Actor | null | undefined,
  ticket: TicketAccessInfo,
): boolean {
  if (!actor || !can(actor, 'ticket.comment.public') || !canSeeTicket(actor, ticket)) return false
  return ticket.raisedBy === actor.id || can(actor, 'ticket.edit.fields')
}

/** The Reopen button: only the raiser. HR reopen by changing status (`ticket.edit.fields`). */
export function canReopenTicket(
  actor: Actor | null | undefined,
  ticket: TicketAccessInfo,
): boolean {
  return !!actor && can(actor, 'ticket.reopen.own') && ticket.raisedBy === actor.id
}

/** Attachments follow the same rule as public comments. */
export function canAttachToTicket(
  actor: Actor | null | undefined,
  ticket: TicketAccessInfo,
): boolean {
  return can(actor, 'ticket.attach') && canCommentOnTicket(actor, ticket)
}

export interface RoleMeta {
  label: string
  color: PaletteColor
  description: string
}

/** Display label, lozenge colour and a one-line explanation for each access level. */
export const ACCESS_META: Record<AccessLevel, RoleMeta> = {
  viewer: {
    label: 'Viewer',
    color: 'slate',
    description:
      'View only: can look at non-confidential tickets in the HR queue, but not change them.',
  },
  employee: {
    label: 'Employee',
    color: 'blue',
    description:
      'Raises tickets, follows them in My tickets, comments and reopens their own tickets.',
  },
  hr: {
    label: 'HR team',
    color: 'violet',
    description:
      'Triages, assigns and resolves tickets; sees non-confidential tickets plus confidential ones assigned to them. Can edit Settings.',
  },
  hrLead: {
    label: 'HR lead',
    color: 'rose',
    description:
      'Everything the HR team can do, plus every confidential ticket and managing who has HR access.',
  },
}

export function roleMeta(actor: Pick<Actor, 'role' | 'isHrLead'>): RoleMeta {
  return ACCESS_META[accessLevel(actor)]
}
